import { useEffect, useState } from 'react';
import { useAnalyticsStore } from '~contexts/mobx';
import { ROAD_TO_REWARDS_CAMPAIGN_ID } from '~containers/analytics/kpis/sources/road-to-rewards-campaign';
import type { KpiCampaignContext } from '~containers/analytics/kpis/types';

/** One promotion's row on the `campaign-coupons-member` `promotion` axis,
 * read as plain numbers/nullable rates rather than raw envelope cells. */
export interface PromotionActivityRow {
    couponsIssued: number;
    couponsRedeemed: number;
    /** Cohort rate: null on a zero denominator (see the envelope's own
     * `computeRate`), never a false 0%. */
    couponRedemptionRate: number | null;
    /** `couponRedemptionRate`'s own numerator, served beside it so a fold
     * across regional copies sums real counts instead of reconstructing them
     * from the rate. `undefined` on an envelope that predates this field;
     * `rewards-inventory.ts`'s fold falls back to reconstruction only then. */
    couponRedemptionCohort?: number;
    issuedMembers: number;
    redeemedMembers: number;
    memberRedemptionRate: number | null;
    /** `memberRedemptionRate`'s own numerator. Unread since the member cells
     * stopped being folded: a one-copy row takes the served rate as it is,
     * and a wider row takes the `couponGroup` axis's. Kept on the row because
     * the endpoint serves it beside the rate it belongs to. */
    memberRedemptionCohort?: number;
    expiredCoupons: number;
}

/** One coupon's member cells, off the `couponGroup` axis: a distinct-phone
 * count over the coupon's regional copies, which is the one figure the
 * per-promotion rows cannot be summed into (a member holding two copies is a
 * row in each). Keyed `<tierRewardId>:<activationCode>`. */
export interface CouponGroupMembersRow {
    issuedMembers: number;
    redeemedMembers: number;
    memberRedemptionRate: number | null;
    /** How many promotions the API counted these members over. The group
     * carries no region, so a row narrowed by the page's coupon filter
     * compares this against its own before reading the figures as its. */
    promotionsCovered: number;
}

export type CampaignPromotionActivityState =
    | { status: 'loading' }
    | {
          status: 'ready';
          byPromotionId: Map<string, PromotionActivityRow>;
          byCouponGroup: Map<string, CouponGroupMembersRow>;
      }
    | { status: 'unavailable'; reason: string };

const ACTIVITY_FAILED =
    'Coupon activity could not be loaded, so the rewards inventory table cannot be read.';

/** A row cell is contractually `number | string | null`; a missing or
 * non-numeric cell reads as 0, matching `create-envelope-source.ts`'s own
 * `finiteCell`. */
const finiteCell = (cell: unknown): number => {
    const n = Number(cell ?? 0);
    return Number.isFinite(n) ? n : 0;
};

/** Unlike `finiteCell`: a `null` cell (the envelope's own "undefined", an
 * empty-denominator rate) stays `null` rather than collapsing to 0. */
const nullableRateCell = (cell: unknown): number | null =>
    typeof cell === 'number' ? cell : null;

/** A cohort cell (a rate's own numerator): present only once the API adds
 * it, so absent/non-numeric reads as `undefined` (not provided) rather than
 * folded in as a real zero. */
const optionalCohortCell = (cell: unknown): number | undefined =>
    typeof cell === 'number' ? cell : undefined;

/** Same discipline for a `couponGroup` member cell, which carries the one
 * figure this endpoint cannot be summed into: absent is unknown, not zero. */
const memberCountCell = (cell: unknown): number | undefined =>
    typeof cell === 'number' && Number.isFinite(cell) ? cell : undefined;

/**
 * The `campaign-coupons-member` endpoint's `promotion` breakdown, always over
 * the campaign-to-date window (never the page's selected Period, which this
 * hook never even takes as an input) so the rewards inventory table cannot
 * drift onto a different window than its own skeleton implies. Bypasses the
 * `KpiSource`/card pipeline: a table row needs every metric on one promotion
 * at once, where that pipeline reads one metric per card.
 */
export const useCampaignPromotionActivity = (
    context: KpiCampaignContext,
    provinces: string[]
): CampaignPromotionActivityState => {
    const analyticsStore = useAnalyticsStore();
    const [state, setState] = useState<CampaignPromotionActivityState>({
        status: 'loading',
    });

    const provincesKey = provinces.join(',');
    const start = context.status === 'ready' ? context.toDate.start : undefined;
    const end = context.status === 'ready' ? context.toDate.end : undefined;
    const unavailableReason =
        context.status === 'unavailable' ? context.reason : undefined;

    useEffect(() => {
        if (unavailableReason !== undefined) {
            setState({ status: 'unavailable', reason: unavailableReason });
            return;
        }
        if (!start || !end) {
            setState({ status: 'loading' });
            return;
        }

        let cancelled = false;

        const load = async () => {
            try {
                const params: Record<string, string> = {
                    campaignId: ROAD_TO_REWARDS_CAMPAIGN_ID,
                    period: 'campaign',
                    granularity: 'total',
                };
                if (provincesKey.length > 0) {
                    params.provinces = provincesKey;
                }
                const env = await analyticsStore.fetchKpiEnvelope(
                    'analytics/campaign-coupons-member',
                    params
                );
                if (cancelled) return;
                if (!env) {
                    setState({
                        status: 'unavailable',
                        reason: ACTIVITY_FAILED,
                    });
                    return;
                }
                // A degraded envelope carries empty breakdowns, which would
                // settle `ready` and draw the whole table at zero issued and
                // zero redeemed as if that were the measurement.
                if (env.degradation) {
                    setState({
                        status: 'unavailable',
                        reason: env.degradation.reason,
                    });
                    return;
                }
                const axis = env.breakdowns.find((b) => b.by === 'promotion');
                const byPromotionId = new Map<string, PromotionActivityRow>();
                for (const row of axis?.rows ?? []) {
                    byPromotionId.set(row.key, {
                        couponsIssued: finiteCell(row.couponsIssued),
                        couponsRedeemed: finiteCell(row.couponsRedeemed),
                        couponRedemptionRate: nullableRateCell(
                            row.couponRedemptionRate
                        ),
                        couponRedemptionCohort: optionalCohortCell(
                            row.couponRedemptionCohort
                        ),
                        issuedMembers: finiteCell(row.issuedMembers),
                        redeemedMembers: finiteCell(row.redeemedMembers),
                        memberRedemptionRate: nullableRateCell(
                            row.memberRedemptionRate
                        ),
                        memberRedemptionCohort: optionalCohortCell(
                            row.memberRedemptionCohort
                        ),
                        expiredCoupons: finiteCell(row.expiredCoupons),
                    });
                }
                const groupAxis = env.breakdowns.find(
                    (b) => b.by === 'couponGroup'
                );
                const byCouponGroup = new Map<string, CouponGroupMembersRow>();
                for (const row of groupAxis?.rows ?? []) {
                    // Not `finiteCell`: a row missing its member cells would
                    // read as a served zero, which the table cannot tell from
                    // a real one. Left out of the map instead, so the row
                    // dashes rather than claiming nobody was issued a coupon.
                    const issuedMembers = memberCountCell(row.issuedMembers);
                    const redeemedMembers = memberCountCell(
                        row.redeemedMembers
                    );
                    const promotionsCovered = memberCountCell(
                        row.promotionsCovered
                    );
                    if (
                        issuedMembers === undefined ||
                        redeemedMembers === undefined
                    ) {
                        continue;
                    }
                    // Without it the row cannot be matched to a coverage, so
                    // it is no more usable than a missing member cell.
                    if (promotionsCovered === undefined) continue;
                    byCouponGroup.set(row.key, {
                        issuedMembers,
                        redeemedMembers,
                        memberRedemptionRate: nullableRateCell(
                            row.memberRedemptionRate
                        ),
                        promotionsCovered,
                    });
                }
                setState({ status: 'ready', byPromotionId, byCouponGroup });
            } catch {
                if (!cancelled) {
                    setState({
                        status: 'unavailable',
                        reason: ACTIVITY_FAILED,
                    });
                }
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [analyticsStore, unavailableReason, start, end, provincesKey]);

    return state;
};
